import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const empty = { name: "", slug: "", description: "", image: "" };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const refresh = () => api.get("/categories").then((r) => setItems(r.data));

  useEffect(() => {
    refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, slug: c.slug, description: c.description || "", image: c.image || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/categories", form);
        toast.success("Category created");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-ultra text-neutral-500">Taxonomy</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-2">Categories</h1>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra font-bold inline-flex items-center gap-2"
          data-testid="admin-new-category-button"
        >
          <Plus className="w-4 h-4" /> New category
        </button>
      </div>

      <div className="mt-10 bg-white border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Name</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Slug</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Description</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {items.map((c) => (
              <tr key={c.id} data-testid={`admin-category-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-neutral-500">{c.slug}</td>
                <td className="px-4 py-3 text-neutral-500 truncate max-w-md">{c.description}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(c)} className="p-2 hover:text-accent" data-testid={`edit-category-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(c)} className="p-2 hover:text-accent" data-testid={`delete-category-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="4" className="px-4 py-10 text-center text-neutral-500 text-sm">
                  No categories yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-auto p-4" data-testid="category-form-modal">
          <div className="bg-white w-full max-w-lg mt-10 p-8 border border-black">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black tracking-tighter">
                {editing ? "Edit category" : "New category"}
              </h2>
              <button onClick={() => setOpen(false)} className="p-2">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <Row label="Name" required>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  data-testid="category-form-name"
                />
              </Row>
              <Row label="Slug">
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  placeholder="auto-generated if empty"
                  data-testid="category-form-slug"
                />
              </Row>
              <Row label="Description">
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-black/10 bg-white p-3 text-sm outline-none focus:border-black"
                  data-testid="category-form-description"
                />
              </Row>
              <Row label="Image URL">
                <input
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  data-testid="category-form-image"
                />
              </Row>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra font-bold" data-testid="category-form-save">
                  Save
                </button>
                <button type="button" onClick={() => setOpen(false)} className="border border-black px-6 py-3 text-xs uppercase tracking-ultra font-bold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      {children}
    </div>
  );
}
